import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AppLayout, PageHeader } from "@/components/Layout";
import { Gavel, Map, Store, HelpCircle, Megaphone, Building2, MapPin, ChevronRight, Info, Scale, Heart, Gamepad2 } from "lucide-react";

const BASE_LINKS = [
  { to: "/shop", label: "Shop Merch", desc: "Official apparel & gear — shipped to your door", icon: Store },
  { to: "/game", label: "Play the Game", desc: "120 Days to Makeover — train a virtual mustang", icon: Gamepad2 },
  { to: "/about", label: "About the Event", desc: "The 120-day journey, classes & auction", icon: Info },
  { to: "/judges", label: "Meet the Judges", desc: "Our official judging panel", icon: Scale },
  { to: "/fan-favorite", label: "Fan Favorite Vote", desc: "Vote for your favorite mustang", icon: Heart },
  { to: "/mustangs?auction=1", label: "Auction Horses", desc: "Browse the mustangs in the sale", icon: Gavel },
  { to: "/map", label: "Event Map", desc: "Arenas, parking, vendors & more", icon: Map },
  { to: "/vendors", label: "Vendors & Food", desc: "Shops and food trucks on site", icon: Store },
  { to: "/announcements", label: "Announcements", desc: "Latest event updates", icon: Megaphone },
  { to: "/faq", label: "FAQ & Info", desc: "Parking, seating, accessibility", icon: HelpCircle },
];

export default function More() {
  const [hasSponsors, setHasSponsors] = useState(false);
  useEffect(() => {
    api.get("/sponsors").then((r) => setHasSponsors((r.data || []).some((s) => s.name))).catch(() => {});
  }, []);

  const links = (() => {
    if (!hasSponsors) return BASE_LINKS;
    const i = BASE_LINKS.findIndex((l) => l.to === "/vendors") + 1;
    return [...BASE_LINKS.slice(0, i), { to: "/sponsors", label: "Sponsors", desc: "Our valued partners", icon: Building2 }, ...BASE_LINKS.slice(i)];
  })();

  const openDirections = () => {
    const q = encodeURIComponent("Henry County Fairgrounds, 1106 Castle Highway, Eminence, KY 40019");
    const ua = navigator.userAgent || "";
    const gmaps = `https://www.google.com/maps/dir/?api=1&destination=${q}`;
    let nativeUrl = null;
    if (/iPhone|iPad|iPod/i.test(ua)) nativeUrl = `maps://maps.apple.com/?daddr=${q}`;
    else if (/Android/i.test(ua)) nativeUrl = `geo:0,0?q=${q}`;
    if (nativeUrl) {
      window.location.href = nativeUrl;
      setTimeout(() => window.open(gmaps, "_blank"), 700);
    } else {
      window.open(gmaps, "_blank");
    }
  };
  return (
    <AppLayout>
      <PageHeader subtitle="Explore" title="More" />
      <div className="space-y-2 px-4 pt-2">
        {links.map(({ to, label, desc, icon: Icon, soon }) => {
          const inner = (
            <>
              <span className="grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-primary"><Icon size={20} /></span>
              <div className="flex-1">
                <p className="font-semibold text-primary">{label}</p>
                <p className="text-xs text-muted-foreground">{desc}</p>
              </div>
              {soon
                ? <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Soon</span>
                : <ChevronRight size={18} className="text-muted-foreground" />}
            </>
          );
          const testid = `more-${label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`;
          return to ? (
            <Link key={label} to={to} data-testid={testid}
              className="flex items-center gap-3 rounded-xl border border-border bg-card p-4 shadow-sm transition-transform hover:-translate-y-0.5 hover:shadow-md">{inner}</Link>
          ) : (
            <div key={label} data-testid={testid} className="flex items-center gap-3 rounded-xl border border-border bg-card p-4 shadow-sm">{inner}</div>
          );
        })}
      </div>

      <div className="mx-4 mt-6 rounded-xl border border-border bg-card p-4 shadow-sm">
        <h3 className="font-display text-lg font-bold text-primary">Event Location</h3>
        <p className="mt-1 flex items-start gap-2 text-sm text-foreground/80">
          <MapPin size={16} className="mt-0.5 shrink-0 text-secondary" />
          Henry County Fairgrounds, 1106 Castle Highway, Eminence, KY 40019
        </p>
        <button data-testid="directions-link" onClick={openDirections}
          className="mt-3 inline-flex h-11 items-center gap-2 rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground active:scale-95">
          Get Directions <ChevronRight size={16} />
        </button>
      </div>

      <footer className="px-4 pt-8 pb-2 text-center">
        <p className="text-xs text-muted-foreground">Bluegrass Mustang Makeover · Free Admission · Oct 30–31, 2026</p>
        <Link to="/admin" data-testid="staff-login-link"
          className="mt-2 inline-block text-xs font-medium text-muted-foreground/70 underline underline-offset-2">
          Staff Login
        </Link>
      </footer>
    </AppLayout>
  );
}
